<template>
  <div class="sort-detail-page">
    <!-- 1. 标题栏 -->
    <van-nav-bar
      title="Inspection Detail"
      class="detail-nav-bar"
      left-text="Back"
      left-arrow
      @click-left="handleGoBack"
    />

    <!-- 2. 时间段选择 + 搜索按钮 -->
    <div class="date-search-container">
      <!-- 日期范围选择触发器 -->
      <div 
        class="date-input" 
        @click="showCalendar = true"
      >
        <van-icon name="calendar-o" class="date-icon" />
        <span class="date-text">
          {{ dateRangeText || 'Select date range' }}
        </span>
      </div>
      
      <!-- 日历组件 -->
      <van-calendar
        v-model="showCalendar"
        type="range"
        :min-date="minDate"
        :max-date="maxDate"
        :default-date="defaultDateRange"
        @confirm="handleCalendarConfirm"
        @close="showCalendar = false"
        class="calendar-popup"
        :show-confirm="false"
        :formatter="calendarFormatter"
      />

      <van-button
        type="primary"
        class="search-btn"
        @click="handleSearch"
        size="small"
      >
        Search
      </van-button>
    </div>

    <!-- 3. objsortNumber 显示 -->
    <div class="sort-number-container">
      <span class="sort-number">{{ sortData.objsortNumber || '—' }}</span>
    </div>

    <!-- 4. 状态、客户信息行 -->
    <div class="info-row first-info-row">
      <div class="info-item">
        <span class="info-label">Status:</span>
        <span class="info-value" :class="getStatusClass(sortData.statusValue)">{{ sortData.statusValue || '—' }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Client:</span>
        <span class="info-value">{{ sortData.clientName || '—' }}</span>
      </div>
    </div>

    <!-- 5. 开始日期、分类信息行 -->
    <div class="info-row second-info-row">
      <div class="info-item">
        <span class="info-label">Start Date:</span>
        <span class="info-value">{{ sortData.startDate || '—' }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Category:</span>
        <span class="info-value">{{ sortData.sortCategoryValue || '—' }}</span>
      </div>
    </div>

    <!-- 6. 统计方块（每行3个，分两行显示） -->
    <div class="stats-container">
      <!-- 第一行 -->
      <div class="stats-row">
        <div class="stat-item total-stat">
          <span class="stat-value">{{ statistics.total || 0 }}</span>
          <span class="stat-label">Total</span>
        </div>
        <div class="stat-item ok-stat">
          <span class="stat-value">{{ statistics.ok || 0 }}</span>
          <span class="stat-label">OK</span>
        </div>
        <div class="stat-item nok-stat">
          <span class="stat-value">{{ statistics.nok || 0 }}</span>
          <span class="stat-label">NOK</span>
        </div>
      </div>
      <!-- 第二行 -->
      <div class="stats-row">
        <div class="stat-item rework-stat">
          <span class="stat-value">{{ statistics.rework || 0 }}</span>
          <span class="stat-label">Rework</span>
        </div>
        <div class="stat-item fallout-stat">
          <span class="stat-value">{{ formatPercentage(statistics.fallout) }}</span>
          <span class="stat-label">Fallout</span>
        </div>
        <div class="stat-item fallout-rate-stat">
          <span class="stat-value">{{ formatPercentage(statistics.falloutRate) }}</span>
          <span class="stat-label">Fallout Rate Before Rework</span>
        </div>
      </div>
    </div>

    <!-- 7. Detail列表 -->
    <van-pull-refresh
      v-model="refreshing"
      @refresh="handleRefresh"
      class="list-refresh-container"
      pulling-text="Pull down to refresh"
      loosing-text="Release to refresh"
      loading-text="Refreshing..."
    >
      <van-list
        v-model="loading"
        :finished="finished"
        finished-text="No more data"
        @load="handleLoadMore"
        class="detail-list"
        :immediate-check="false"
        loading-text="Loading..."
      >
        <!-- 列表项 -->
        <van-cell
          v-for="(item, index) in detailList"
          :key="index"
          class="list-item"
          @click="handleItemClick(item)"
        >
          <div class="item-inner">
            <div class="item-top">
              <span class="item-date">{{ item.businessDate || '—' }}</span>
              <span class="item-partno">{{ item.partNumber || '—' }}</span>
            </div>
            <div class="item-location">
              <van-icon name="location-o" class="location-icon" />
              <span>{{ item.locationName || '—' }}</span>
            </div>
            <div class="item-stats">
              <div class="list-stat-item ok-item">OK: {{ item.ok || 0 }}</div>
              <div class="list-stat-item nok-item">NOK: {{ item.nok || 0 }}</div>
              <div class="list-stat-item rework-item">Rework: {{ item.rework || 0 }}</div>
              <div class="list-stat-item total-item">Total: {{ item.total || 0 }}</div>
            </div>
            <div class="item-rates">
              <div class="rate-item">Fallout: {{ formatPercentage(item.fallout) }}</div>
              <div class="rate-item">Before Rework: {{ formatPercentage(item.falloutRate) }}</div>
            </div>
          </div>
        </van-cell>

        <!-- 空数据提示 -->
        <div v-if="detailList.length === 0 && !loading" class="empty-tip">
          <van-empty description="No detail data found" />
        </div>
      </van-list>
    </van-pull-refresh>

    <!-- ActionSheet 弹窗 -->
    <van-action-sheet
      v-model="showActionSheet"
      :title="null"
      class="custom-action-sheet"
      :round="false"
      :closeable="true"
      @close="handleActionSheetClose"
    >
      <div class="action-sheet-close" @click="handleActionSheetClose">
        <van-icon name="close" size="20" color="#2a4062" />
      </div>
      
      <div class="action-sheet-content">
        <!-- NOK原因和图片 -->
        <div class="nok-section" v-if="isNokDataValid">
          <div class="nok-title" v-if="nokMode === 'sn'">NOK List</div>
          <div class="nok-list">
            <!-- 新结构：按 SN 展示 -->
            <template v-if="nokMode === 'sn'">
              <div 
                v-for="(item, index) in nokData" 
                :key="item.id || index" 
                class="nok-item"
                :class="{ 'last-item': index === nokData.length - 1 }"
                v-if="isValidItem(item)"
              >
                <div class="sn-line">SN: {{ item.sn }}</div>
                <div class="reason-block">
                  <div class="reason-label">NOK Reason:</div>
                  <div class="reason-tags">
                    <van-tag
                      v-for="(r, rIndex) in (item.reasonList || [])"
                      :key="rIndex"
                      type="primary"
                      plain
                      class="reason-tag"
                    >{{ r }}</van-tag>
                    <span v-if="!item.reasonList || item.reasonList.length === 0" class="reason-empty">-</span>
                  </div>
                </div>

                <div class="pictures-label">Pictures:</div>
                <div class="image-list" v-if="hasValidUrlList(item)">
                  <div 
                    v-for="(url, imgIndex) in item.urlList" 
                    :key="imgIndex" 
                    class="image-item"
                    @click="previewImage(item.urlList, imgIndex)"
                  >
                    <van-image
                      width="80"
                      height="80"
                      fit="cover"
                      :src="url"
                      class="nok-image"
                    >
                      <template v-slot:loading>
                        <van-loading type="spinner" size="20" />
                      </template>
                    </van-image>
                  </div>
                </div>
                <div class="no-images" v-else>
                  No images available
                </div>
                
                <div class="nok-divider" v-if="index !== nokData.length - 1"></div>
              </div>
            </template>

            <!-- 旧结构：完全保持以前的 DOM -->
            <template v-else>
              <div 
                v-for="(item, index) in nokData" 
                :key="index" 
                class="nok-item"
                :class="{ 'last-item': index === nokData.length - 1 }"
                v-if="isValidItem(item)"
              >
                <div class="nok-reason">{{ getNokReason(item) }}</div>
                <div class="image-list" v-if="hasValidUrlList(item)">
                  <div 
                    v-for="(url, imgIndex) in item.urlList" 
                    :key="imgIndex" 
                    class="image-item"
                    @click="previewImage(item.urlList, imgIndex)"
                  >
                    <van-image
                      width="80"
                      height="80"
                      fit="cover"
                      :src="url"
                      class="nok-image"
                    >
                      <template v-slot:loading>
                        <van-loading type="spinner" size="20" />
                      </template>
                    </van-image>
                  </div>
                </div>
                <div class="no-images" v-else>
                  No images available
                </div>
                
                <div class="nok-divider" v-if="index !== nokData.length - 1"></div>
              </div>
            </template>
          </div>
        </div>
        
        <!-- 没有NOK数据的情况 -->
        <div class="no-nok-data" v-else>
          <div class="empty-state">
            <van-icon name="photo-o" size="48" color="#ccc" />
            <div class="empty-text">No NOK data available</div>
          </div>
        </div>
      </div>
    </van-action-sheet>

    <!-- 图片预览 -->
    <van-image-preview
      v-model="showImagePreview"
      :images="previewImages"
      :start-position="previewIndex"
    >
    </van-image-preview>
  </div>
</template>

<script>
import objstorage from "@/common/localstorage/storage.js";
import objaxios from "axios";
import objurl from "@/common/interface/url.js";

export default {
  name: "SortDetailMobile",
  data() {
    return {
      // 接收主页传递的sortData
      sortData: {},
      // 日历控制
      showCalendar: false,
      minDate: new Date(2020, 0, 1),
      maxDate: new Date(2030, 11, 31),
      defaultDateRange: [], // 默认选中的日期范围
      selectedDateRange: [], // 存储选中的日期对象数组
      dateRangeText: "", // 显示的日期文本
      // 列表请求参数
      queryParams: {
        pageNum: 1,
        pageSize: 10,
        sortId: "",
        businessDateStart: "",
        businessDateEnd: ""
      },
      // 列表数据
      detailList: [],
      // 列表加载状态
      loading: false,
      finished: false,
      refreshing: false,
      // 统计数据
      statistics: {
        total: 0,
        ok: 0,
        nok: 0,
        rework: 0,
        fallout: 0,
        falloutRate: 0
      },
      // ActionSheet 相关
      showActionSheet: false,
      currentItem: {},
      nokData: [],
      nokMode: "legacy", // legacy: nokReason+urlList; sn: snList
      // 图片预览相关
      showImagePreview: false,
      previewImages: [],
      previewIndex: 0
    };
  },
  computed: {
    // 检查nokData是否有效
    isNokDataValid() {
      return Array.isArray(this.nokData) && this.nokData.length > 0;
    }
  },
  created() {
    this.parseSortData();
    this.initDateRange();
    this.initQueryParams();
    this.getDetailList();
    this.getStatistics();
  },
  methods: {
    parseSortData() {
      const sortDataStr = this.$route.query.sortData;
      if (sortDataStr) {
        try {
          this.sortData = JSON.parse(sortDataStr);
          console.log("Parsed sort data:", this.sortData);
        } catch (e) {
          console.error("Failed to parse sortData:", e);
          this.$toast.fail("Data parsing error");
          this.handleGoBack();
        }
      } else {
        this.$toast.fail("No data received");
        this.handleGoBack();
      }
    },

    // 初始化日期范围（默认近一周，与PC端保持一致）
    initDateRange() {
      const end = new Date();
      const start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7); // 7天前，与PC端一致
      
      this.defaultDateRange = [start, end];
      this.selectedDateRange = [start, end];
      
      // 格式化显示文本
      this.dateRangeText = this.formatDateRangeText(start, end);
      
      // 设置查询参数
      this.queryParams.businessDateStart = this.formatDate(start);
      this.queryParams.businessDateEnd = this.formatDate(end);
    },

    // 格式化日期为YYYY-MM-DD（与PC端保持一致）
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    // 格式化日期范围显示文本
    formatDateRangeText(start, end) {
      const format = (date) => {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${month}/${day}`;
      };
      return `${format(start)} - ${format(end)}`;
    },

    // 日历确认选择
    handleCalendarConfirm(date) {
      if (date && date.length === 2) {
        this.selectedDateRange = date;
        this.showCalendar = false;
        
        // 更新显示文本
        this.dateRangeText = this.formatDateRangeText(date[0], date[1]);
        
        // 更新查询参数
        this.queryParams.businessDateStart = this.formatDate(date[0]);
        this.queryParams.businessDateEnd = this.formatDate(date[1]);
      } else {
        this.$toast('Please select a date range');
      }
    },

    // 日历格式化器 - 移除中文显示
    calendarFormatter(day) {
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      
      if (day.type === 'start') {
        day.bottomInfo = 'Start';
      } else if (day.type === 'end') {
        day.bottomInfo = 'End';
      } else if (day.type === 'start-end') {
        day.bottomInfo = 'Start/End';
      }
      
      return day;
    },

    initQueryParams() {
      if (this.sortData.id) {
        this.queryParams.sortId = this.sortData.id;
      }
      this.queryParams.pageNum = 1;
      this.queryParams.pageSize = 10;
    },

    handleGoBack() {
      this.$router.go(-1);
    },

    handleSearch() {
      this.detailList = [];
      this.queryParams.pageNum = 1;
      this.finished = false;
      this.getDetailList();
      this.getStatistics();
    },

    handleRefresh() {
      this.queryParams.pageNum = 1;
      this.finished = false;
      this.getDetailList(true);
      this.getStatistics();
    },

    handleLoadMore() {
      this.getDetailList();
    },

    // 列表项点击事件
    handleItemClick(item) {
      console.log("Detail item clicked:", item);
      this.currentItem = item;
      this.showActionSheet = true;
      this.getNokData(item);
    },

    // 获取NOK数据
    getNokData(item) {
      const userInfo = objstorage.get("userinfo");
      if (!userInfo || !userInfo.tokenHead || !userInfo.token) {
        this.$toast.fail("Login expired");
        objstorage.remove("userinfo");
        this.$router.replace("/");
        return;
      }

      objaxios.defaults.headers.common["Authorization"] = userInfo.tokenHead + userInfo.token;

      this.$toast.loading({
        message: 'Loading...',
        forbidClick: true,
        duration: 0
      });

      objaxios
        .post(objurl.report_selectNokPhoto_mobile, item)
        .then((res) => {
          this.$toast.clear();
          if (res.data.code === "200") {
            const OSS_BASE = "https://hqsautomotive.oss-us-west-1.aliyuncs.com/";
            let data = res.data.data || [];
            if (!Array.isArray(data)) data = [data];

            const hasSnList = data.some(
              (d) => Array.isArray(d && d.snList) && d.snList.length > 0
            );

            if (hasSnList) {
              this.nokMode = "sn";
              const snItems = [];
              data.forEach((group) => {
                const snList = Array.isArray(group && group.snList) ? group.snList : [];
                snList.forEach((snItem) => {
                  const reasons = Array.isArray(snItem && snItem.snReasonList)
                    ? snItem.snReasonList
                    : [];
                  const pictures = Array.isArray(snItem && snItem.snPictureList)
                    ? snItem.snPictureList
                    : [];

                  snItems.push({
                    id: snItem && snItem.id,
                    sn: snItem && snItem.sn,
                    reasonList: reasons
                      .map((r) => (r && (r.dictValue || r.dictKey)) || "")
                      .filter(Boolean),
                    urlList: pictures
                      .map((p) => (p && p.picture) || "")
                      .filter(Boolean)
                      .map((p) => OSS_BASE + p),
                  });
                });
              });
              this.nokData = snItems;
            } else {
              this.nokMode = "legacy";
              data.forEach((val) => {
                if (val && val.urlList && Array.isArray(val.urlList)) {
                  val.urlList = val.urlList
                    .filter(Boolean)
                    .map((url) => OSS_BASE + url);
                }
              });
              this.nokData = data;
            }

            console.log("NOK data loaded:", this.nokData);
          } else if (res.data.code === "401") {
            this.$toast.fail(res.data.message || "Login expired");
            objstorage.remove("userinfo");
            this.$router.replace("/");
          } else {
            this.$toast.fail(res.data.message || "Failed to load NOK data");
            this.nokData = [];
            this.nokMode = "legacy";
          }
        })
        .catch((error) => {
          this.$toast.clear();
          console.error("NOK data request error:", error);
          this.$toast.fail("Network error, please try again");
          this.nokData = [];
          this.nokMode = "legacy";
        });
    },

    // 图片预览
    previewImage(urlList, index) {
      if (Array.isArray(urlList)) {
        this.previewImages = urlList;
        this.previewIndex = index;
        this.showImagePreview = true;
      }
    },

    // 获取详情列表数据
    getDetailList(isRefresh = false) {
      const userInfo = objstorage.get("userinfo");
      if (!userInfo || !userInfo.tokenHead || !userInfo.token) {
        this.$toast.fail("Login expired");
        objstorage.remove("userinfo");
        this.$router.replace("/");
        return;
      }

      objaxios.defaults.headers.common["Authorization"] = userInfo.tokenHead + userInfo.token;

      if (isRefresh) {
        this.refreshing = true;
      } else {
        this.loading = true;
      }

      objaxios
        .post(objurl.sort_detail_mobile, this.queryParams)
        .then((res) => {
          this.loading = false;
          this.refreshing = false;

          if (res.data.code === "200") {
            const resData = res.data.data || {};
            const list = resData.list || [];

            if (isRefresh || this.queryParams.pageNum === 1) {
              this.detailList = list;
            } else {
              this.detailList = this.detailList.concat(list);
            }

            const total = Number(resData.total) || 0;
            this.finished = this.detailList.length >= total;

            this.queryParams.pageNum += 1;
          } else if (res.data.code === "401") {
            this.$toast.fail(res.data.message || "Login expired");
            objstorage.remove("userinfo");
            this.$router.replace("/");
          } else {
            this.$toast.fail(res.data.message || "Failed to get detail data");
          }
        })
        .catch((error) => {
          console.error("Detail list request error:", error);
          this.loading = false;
          this.refreshing = false;
          this.$toast.fail("Network error, please try again");
        });
    },

    // 获取统计数据
    getStatistics() {
      const userInfo = objstorage.get("userinfo");
      if (!userInfo || !userInfo.tokenHead || !userInfo.token) {
        return;
      }

      objaxios.defaults.headers.common["Authorization"] = userInfo.tokenHead + userInfo.token;

      // 使用与详情列表相同的查询参数
      objaxios
        .post(objurl.record_statistics_mobile, this.queryParams)
        .then((res) => {
          if (res.data.code === "200") {
            this.statistics = res.data.data || {};
            console.log("Statistics data:", this.statistics);
          } else if (res.data.code === "401") {
            this.$toast.fail(res.data.message || "Login expired");
            objstorage.remove("userinfo");
            this.$router.replace("/");
          } else {
            console.error("Failed to get statistics:", res.data.message);
          }
        })
        .catch((error) => {
          console.error("Statistics request error:", error);
        });
    },

    // 格式化百分比显示（与PC端保持一致）
    formatPercentage(value) {
      if (value === null || value === undefined) return "0%";
      // 限制为两位小数
      return parseFloat(value).toFixed(2) + "%";
    },

    getStatusClass(status) {
      if (!status) return "";
      const statusLower = status.toLowerCase();
      if (statusLower === "active") {
        return "status-active";
      } else if (statusLower === "closed") {
        return "status-closed";
      } else {
        return "status-default";
      }
    },

    // 处理弹窗关闭
    handleActionSheetClose() {
      this.showActionSheet = false;
      // 清空数据，避免关闭时仍在渲染
      this.nokData = [];
      this.nokMode = "legacy";
      this.currentItem = {};
    },

    // 检查item是否有效
    isValidItem(item) {
      return item !== null && item !== undefined && typeof item === 'object';
    },

    // 获取NOK原因，避免直接访问可能不存在的属性
    getNokReason(item) {
      if (this.isValidItem(item) && item.nokReason) {
        return item.nokReason;
      }
      return 'No reason provided';
    },

    // 检查是否有有效的图片列表
    hasValidUrlList(item) {
      return this.isValidItem(item) && item.urlList && Array.isArray(item.urlList) && item.urlList.length > 0;
    }
  }
};
</script>

<style lang="stylus" scoped>
.sort-detail-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-bottom: 20px;
  box-sizing: border-box;
}

/* 标题栏样式 */
.detail-nav-bar {
  background-color: #2a4062;
  color: #fff;
  height: 56px;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  box-sizing: border-box;

  /deep/ .van-nav-bar__content {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
  }

  /deep/ .van-nav-bar__title {
    color: #fff;
    font-weight: 500;
    font-size: 18px;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    margin: 0;
    line-height: 56px;
  }

  /deep/ .van-nav-bar__left {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex: 1;
    min-width: 24px;
    padding: 0;
  }

  /deep/ .van-nav-bar__text,
  /deep/ .van-nav-bar__arrow {
    color: #fff;
  }
}

/* 日期选择 + 搜索容器 */
.date-search-container {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #fff;
  margin: 10px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  gap: 10px;
}

/* 日期输入框样式 */
.date-input {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #f5f7fa;
  border-radius: 6px;
  font-size: 14px;
  color: #303133;
  cursor: pointer;
  min-height: 36px;
}

.date-icon {
  color: #909399;
  font-size: 16px;
  margin-right: 8px;
}

.date-text {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 搜索按钮 */
.search-btn {
  background-color: #2a4062;
  color: #fff;
  min-width: 80px;
}

/* 日历弹窗样式 */
.calendar-popup {
  /deep/ .van-calendar__header {
    background-color: #2a4062;
    color: #fff;
  }
  
  /deep/ .van-calendar__month-title {
    color: #303133;
    font-weight: 500;
  }
  
  /deep/ .van-calendar__day--start,
  /deep/ .van-calendar__day--end,
  /deep/ .van-calendar__day--start-end {
    background-color: #2a4062;
    color: #fff;
  }
  
  /deep/ .van-calendar__day--in-range {
    background-color: rgba(42, 64, 98, 0.1);
  }
  
  /deep/ .van-calendar__bottom-info {
    font-size: 10px;
    line-height: 1.2;
  }
}

/* objsortNumber 样式 */
.sort-number-container {
  padding: 0 16px;
  margin: 15px 0;
}

.sort-number {
  font-size: 24px;
  font-weight: 600;
  color: #2a4062;
}

/* 信息行样式 */
.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0 16px;
  margin-bottom: 10px;
  font-size: 14px;
}

.first-info-row {
  margin-top: 5px;
}

.second-info-row {
  margin-bottom: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
  width: 48%;
}

.info-label {
  color: #909399;
  margin-bottom: 2px;
  font-size: 16px;
}

.info-value {
  color: #303133;
  font-weight: 500;
}

.status-active {
  color: #4caf50;
}

.status-closed {
  color: #f44336;
}

.status-default {
  color: #2196f3;
}

/* 统计方块样式 - 每行3个，分两行显示，调整尺寸 */
.stats-container {
  padding: 0 16px;
  margin-bottom: 20px;
}

.stats-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 10px;
}

.stat-item {
  flex: 1;
  padding: 16px 8px;
  border-radius: 10px;
  text-align: center;
  color: #fff;
  box-sizing: border-box;
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 0;
}

.total-stat {
  background-color: #2a4062;
}

.ok-stat {
  background-color: #4caf50;
}

.nok-stat {
  background-color: #f44336;
}

.rework-stat {
  background-color: #ff9800;
}

.fallout-stat {
  background-color: #5fa8dd;
}

.fallout-rate-stat {
  background-color: #b98aea;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 6px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: center;
  line-height: 1.2;
}

.stat-label {
  font-size: 12px;
  opacity: 0.95;
  white-space: normal;
  line-height: 1.3;
  width: 100%;
  text-align: center;
  word-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 列表样式 */
.list-refresh-container {
  padding: 0 16px;
}

.detail-list {
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.list-item {
  /deep/ .van-cell {
    border-bottom: 1px solid #f5f5f5;
    padding: 12px 16px;
  }
  &:last-child /deep/ .van-cell {
    border-bottom: none;
  }
}

.item-inner {
  width: 100%;
}

.item-top {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.item-date {
  color: #2a4062;
}

.item-partno {
  color: #606266;
  font-weight: normal;
}

.item-location {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 13px;
  color: #909399;
}

.location-icon {
  font-size: 14px;
  margin-right: 4px;
}

.item-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.list-stat-item {
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  min-width: 70px;
  text-align: center;
  flex: none;
  height: auto;
}

.ok-item {
  background-color: #e8f5e9 !important;
  color: #4caf50 !important;
}

.nok-item {
  background-color: #ffebee !important;
  color: #f44336 !important;
}

.rework-item {
  background-color: #fff3e0 !important;
  color: #ff9800 !important;
}

.total-item {
  background-color: #e3f2fd !important;
  color: #2196f3 !important;
}

.item-rates {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #909399;
}

.rate-item {
  flex: 1;
}

/* 空数据提示 */
.empty-tip {
  padding: 40px 0;
  text-align: center;
}

/* ActionSheet 样式 */
.custom-action-sheet {
  /deep/ .van-action-sheet__header {
    display: none;
  }

  /deep/ .van-action-sheet__close {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10;
    color: #2a4062;
    font-size: 18px;
    display: block !important;
  }

  /deep/ .van-action-sheet__content {
    padding-top: 40px !important;
  }
}

.action-sheet-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 100;
  cursor: pointer;
  padding: 8px;
}

.action-sheet-content {
  max-height: 70vh;
  overflow-y: auto;
  padding: 20px 16px;
  background-color: #fff;
}

.nok-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 14px;
}

.nok-list {
  .nok-item {
    margin-bottom: 24px;
    padding: 0;
    background-color: #fff !important;
    
    &.last-item {
      margin-bottom: 0;
    }
  }

  .nok-reason {
    font-size: 15px;
    font-weight: 500;
    color: #303133;
    margin-bottom: 16px;
    line-height: 1.4;
    text-align: center;
  }

  .sn-line {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
  }

  .reason-block {
    margin-bottom: 10px;
  }

  .reason-label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 6px;
  }

  .reason-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .reason-tag {
    max-width: 100%;
  }

  .reason-empty {
    color: #c0c4cc;
    font-size: 12px;
  }

  .pictures-label {
    font-size: 12px;
    color: #909399;
    margin-top: 6px;
  }

  .image-list {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start !important;
    gap: 12px;
    width: 100%;
    background-color: #fff !important;
    padding: 8px 0;
  }

  .image-item {
    cursor: pointer;
    transition: transform 0.2s;
    flex: 0 0 auto;
    
    &:active {
      transform: scale(0.95);
    }
  }

  .nok-image {
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    background-color: #f8f8f8;
  }

  .no-images {
    color: #909399;
    font-size: 14px;
    text-align: center;
    padding: 20px 0;
    background-color: #fff;
  }

  .nok-divider {
    height: 1px;
    background-color: #f0f0f0;
    margin-top: 24px;
    width: 100%;
  }
}

.no-nok-data {
  padding: 40px 20px;
  text-align: center;
  background-color: #fff;
  
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  
  .empty-text {
    margin-top: 12px;
    color: #909399;
    font-size: 14px;
  }
}

/* 适配小屏手机 */
@media (max-width: 320px) {
  .stats-row {
    gap: 6px;
  }
  
  .stat-item {
    height: 80px;
    padding: 12px 4px;
  }
  
  .stat-value {
    font-size: 18px;
  }
  
  .stat-label {
    font-size: 11px;
  }
  
  .list-stat-item {
    padding: 5px 8px;
    font-size: 11px;
    min-width: 60px;
  }
  
  .action-sheet-content {
    padding: 16px 12px;
  }
  
  .nok-list .image-list {
    gap: 8px;
  }
  
  .nok-image {
    width: 70px;
    height: 70px;
  }
  
  .action-sheet-close {
    top: 12px;
    right: 12px;
  }
}

/* 适配大屏手机和平板 */
@media (min-width: 414px) {
  .stats-row {
    gap: 12px;
  }
  
  .stat-item {
    height: 95px;
    padding: 18px 8px;
  }
  
  .stat-value {
    font-size: 22px;
  }
  
  .stat-label {
    font-size: 13px;
  }
  
  .list-stat-item {
    padding: 7px 12px;
    font-size: 13px;
    min-width: 75px;
  }
  
  .action-sheet-content {
    padding: 24px 20px;
  }
  
  .nok-list .image-list {
    gap: 16px;
  }
  
  .nok-image {
    width: 90px;
    height: 90px;
  }
  
  .action-sheet-close {
    top: 20px;
    right: 20px;
  }
}

/* 防止文字超出的额外保护 */
@media (max-width: 360px) {
  .stat-label {
    font-size: 11px;
    -webkit-line-clamp: 2;
  }
  
  .stat-value {
    font-size: 18px;
  }
}
</style>


// WEBPACK FOOTER //
// src/views/SortDetailMobile.vue